import React, { useEffect, useState } from 'react';
import { X, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import api from '../utils/api';

const PromoPopup = () => {
  const { rawLang, t, localize } = useLang();
  const [open, setOpen] = useState(false);
  const [promos, setPromos] = useState([]);
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!rawLang) return;
    const dismissed = sessionStorage.getItem('4an_promo_dismissed');
    if (dismissed) return;
    api.get('/promotions').then(({ data }) => {
      if (!data || data.length === 0) return;
      // Prefer promotions explicitly marked show_on_landing; fall back to all.
      const flagged = data.filter((p) => p.show_on_landing);
      const list = (flagged.length > 0 ? flagged : data)
        .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999))
        .slice(0, 5);
      setPromos(list);
      setTimeout(() => setOpen(true), 1200);
    }).catch(() => {});
  }, [rawLang]);

  const handleClose = () => {
    sessionStorage.setItem('4an_promo_dismissed', '1');
    setOpen(false);
  };

  const handleCTA = (promo) => {
    sessionStorage.setItem('4an_promo_dismissed', '1');
    setOpen(false);
    navigate(`/offers?highlight=${promo.id}`);
  };

  if (!open || promos.length === 0) return null;

  const promo = promos[index];
  const savingsPct = promo.total_price ? Math.round(((promo.total_price - promo.offer_price) / promo.total_price) * 100) : 0;
  const next = () => setIndex((i) => (i + 1) % promos.length);
  const prev = () => setIndex((i) => (i - 1 + promos.length) % promos.length);

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-6" data-testid="promo-popup">
      <div className="relative bg-gradient-to-br from-[#0d3b25] to-[#0a2e1e] border-2 border-[#c5a059] rounded-sm max-w-lg w-full overflow-hidden shadow-2xl fade-up">
        <button onClick={handleClose} data-testid="promo-close" className="absolute top-4 right-4 text-[#c5a059] hover:text-white transition-colors z-10">
          <X className="w-6 h-6" />
        </button>

        {promos.length > 1 && (
          <>
            <button
              onClick={prev}
              data-testid="promo-prev"
              aria-label="Previous"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-[#c5a059]/20 hover:bg-[#c5a059]/40 text-[#c5a059] flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              data-testid="promo-next"
              aria-label="Next"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-[#c5a059]/20 hover:bg-[#c5a059]/40 text-[#c5a059] flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        <div className="px-10 pt-12 pb-8 text-center text-white">
          <div className="inline-flex items-center px-4 py-1 border border-[#c5a059] rounded-full text-[#c5a059] text-xs tracking-[0.2em] mb-6">
            <Sparkles className="w-3 h-3 mr-2" />
            {(t.promo.title || 'Bingo Beauty Deals').toUpperCase()}
          </div>
          <h2 className="text-3xl font-serif font-bold mb-2">{localize(promo, 'title')}</h2>
          <p className="text-white/70 text-sm mb-6 line-clamp-2">{localize(promo, 'description') || t.promo.subtitle}</p>
          <div className="flex items-baseline justify-center space-x-4 mb-6">
            <span className="text-white/50 line-through text-xl">€{promo.total_price?.toFixed(0)}</span>
            <span className="text-[#c5a059] font-serif font-bold text-5xl">€{promo.offer_price?.toFixed(0)}</span>
          </div>
          {savingsPct > 0 && (
            <div className="inline-block bg-[#c5a059] text-[#0d3b25] px-6 py-2 rounded-sm font-semibold text-sm mb-6">
              {t.common.save} {savingsPct}%
            </div>
          )}
          <button
            onClick={() => handleCTA(promo)}
            data-testid="promo-cta"
            className="block w-full bg-[#c5a059] text-[#0d3b25] py-4 font-semibold hover:bg-[#d4b56e] transition-colors rounded-sm"
          >
            {t.promo.cta || 'View this deal'}
          </button>

          {promos.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-5" data-testid="promo-dots">
              {promos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  aria-label={`Deal ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === index ? 'w-8 bg-[#c5a059]' : 'w-1.5 bg-white/30'}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoPopup;
